import { FileFormats } from '@common/Transpiler/types';
import { PipelineEntityType } from '@pages/Transpiler/config';
import { PlusIcon } from '@prophecy/icons';
import { Button } from '@prophecy/ui-v3/Button';
import { usePersistentCallback } from '@prophecy/utils/react/hooks';
import { isUniqueID, uniqueIDWithDelimiter } from 'frontend/core/src/utils';
import { useMemo } from 'react';

import { useImportState } from '../../useImportState';
import { isInformatica } from '../../utils';
import { ActionTypes, FileTypes, TreeItem } from '../types';
import { formatPipelineName, PipelineExtensions } from '../utils';

export const AddAllPipelines: React.FC = () => {
  const {
    projectBrowserState: { treeData: filteredTreeData },
    transpilerState: {
      transpilerEntityTypes: { pipelines }
    },
    formDataWithActions: {
      formContext: { getValues }
    },
    actions: { addAllPipelines, updateTranspiledPipeline }
  } = useImportState();

  const source = getValues('source') as FileFormats;

  const pipelineNames = useMemo(() => pipelines.map((pipeline) => pipeline.name), [pipelines]);

  const getAllPipelines = usePersistentCallback(
    (currentPipelines: PipelineEntityType[] = [], names: string[], items: TreeItem[]) => {
      items.forEach((node) => {
        const fileType = node.fileType as FileTypes;
        const isPipelineNode = PipelineExtensions.includes(fileType);
        const isLeaf = node.isLeaf;
        if (isPipelineNode && isLeaf && node.selectable) {
          const name = formatPipelineName(node.title as string, fileType);
          const currentName = isUniqueID(name, pipelines as unknown as { [key: string]: string }[], 'name')
            ? name
            : uniqueIDWithDelimiter(name, names);
          names.push(currentName);
          currentPipelines.push({
            name: currentName,
            filePath: node.key as string,
            __pathIndex: pipelines.length + currentPipelines.length,
            enabled: true
          });
        }
        const children = (node.children || []) as TreeItem[];
        if (!isLeaf && children.length) {
          currentPipelines = getAllPipelines(currentPipelines, names, children);
        }
      });
      return currentPipelines;
    }
  );

  const addAllAlteryxPipelines = () => {
    addAllPipelines(getAllPipelines([], pipelineNames, filteredTreeData));
  };

  const addAllInformaticaPipelines = () => {
    const pipelines = getAllPipelines([], pipelineNames, filteredTreeData);
    pipelines.forEach((pipeline) => updateTranspiledPipeline(0, pipeline, ActionTypes.add));
  };

  return (
    (source === FileFormats.alteryx || isInformatica(source as FileFormats)) && (
      <Button
        variant='secondaryGray'
        size='s'
        onClick={isInformatica(source) ? addAllInformaticaPipelines : addAllAlteryxPipelines}
        icon={<PlusIcon type='default' />}
        iconPlacement='left'
        data-test-id='project-browser-add-all-pipelines-button'>
        Add all pipelines
      </Button>
    )
  );
};
